import { FC } from "react";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { BasicDialog } from "../../components/BasicDialog";
import {
	APP_VERSION_PLATFORMS,
	AppVersion,
	AppVersionPlatform,
	MOBILE_PLATFORMS,
	SaveAppVersion,
} from "../../domain/appVersion.interface";

/** Same shape the backend enforces with `@IsSemVer()`. */
const SEMVER =
	/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(-[0-9A-Za-z.-]+)?(\+[0-9A-Za-z.-]+)?$/;

const schema = yup.object({
	platform: yup
		.mixed<AppVersionPlatform>()
		.oneOf(Object.values(AppVersionPlatform), "Select a component")
		.required("Select a component"),
	version: yup
		.string()
		.trim()
		.required("Version is required")
		.matches(SEMVER, "Use the MAJOR.MINOR.PATCH format, e.g. 2.3.0"),
	apiVersion: yup.string().trim().max(50).optional(),
	functionalVersion: yup.string().trim().max(50).optional(),
	releaseNotes: yup.string().trim().max(2000).optional(),
	storeUrl: yup
		.string()
		.trim()
		.optional()
		.matches(/^(https:\/\/.+)?$/, "Must be an https:// URL"),
});

type FormValues = yup.InferType<typeof schema>;

/** Empty optional fields are omitted so the backend does not store "". */
const toPayload = (values: FormValues): SaveAppVersion => {
	const optional = (value?: string): string | undefined =>
		value?.trim() ? value.trim() : undefined;
	return {
		platform: values.platform,
		version: values.version.trim(),
		apiVersion: optional(values.apiVersion),
		functionalVersion: optional(values.functionalVersion),
		releaseNotes: optional(values.releaseNotes),
		storeUrl: MOBILE_PLATFORMS.includes(values.platform)
			? optional(values.storeUrl)
			: undefined,
	};
};

interface AppVersionDialogProps {
	/** Version being edited; absent when creating. */
	appVersion?: AppVersion;
	isSaving?: boolean;
	onCancel: () => void;
	onSave: (data: SaveAppVersion) => void;
}

const AppVersionDialog: FC<AppVersionDialogProps> = ({
	appVersion,
	isSaving,
	onCancel,
	onSave,
}) => {
	const {
		control,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm<FormValues>({
		resolver: yupResolver(schema),
		mode: "all",
		defaultValues: {
			platform: appVersion?.platform ?? AppVersionPlatform.ANDROID,
			version: appVersion?.version ?? "",
			apiVersion: appVersion?.apiVersion ?? "",
			functionalVersion: appVersion?.functionalVersion ?? "",
			releaseNotes: appVersion?.releaseNotes ?? "",
			storeUrl: appVersion?.storeUrl ?? "",
		},
	});

	const isMobile = MOBILE_PLATFORMS.includes(watch("platform"));

	return (
		<BasicDialog
			title={appVersion ? "Edit version" : "Add version"}
			onCancel={onCancel}
			onSubmit={handleSubmit((values) => onSave(toPayload(values)))}
			isLoading={isSaving}
			sx={{ "& .MuiDialog-paper": { maxWidth: 480 } }}
		>
			<Stack spacing={2} sx={{ pt: 1 }}>
				<Controller
					name="platform"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							select
							label="Component"
							// The platform decides which "current" a version competes for;
							// moving a saved version to another component would be confusing.
							disabled={!!appVersion}
							error={!!errors.platform}
							helperText={errors.platform?.message}
							inputProps={{ "data-testid": "app-version-platform" }}
						>
							{APP_VERSION_PLATFORMS.map((p) => (
								<MenuItem key={p.value} value={p.value}>
									{p.label}
								</MenuItem>
							))}
						</TextField>
					)}
				/>
				<Controller
					name="version"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							label="Version"
							placeholder="2.3.0"
							required
							error={!!errors.version}
							helperText={errors.version?.message}
							inputProps={{ "data-testid": "app-version-version" }}
						/>
					)}
				/>
				<Controller
					name="apiVersion"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							label="API version"
							error={!!errors.apiVersion}
							helperText={errors.apiVersion?.message}
						/>
					)}
				/>
				<Controller
					name="functionalVersion"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							label="Functional version"
							error={!!errors.functionalVersion}
							helperText={errors.functionalVersion?.message}
						/>
					)}
				/>
				{isMobile ? (
					<Controller
						name="storeUrl"
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label="Store URL"
								error={!!errors.storeUrl}
								helperText={
									errors.storeUrl?.message ??
									"Optional. The app builds each brand's store link itself; this is the fallback."
								}
								inputProps={{ "data-testid": "app-version-store-url" }}
							/>
						)}
					/>
				) : null}
				<Controller
					name="releaseNotes"
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							label="Release notes"
							multiline
							minRows={3}
							error={!!errors.releaseNotes}
							helperText={errors.releaseNotes?.message}
						/>
					)}
				/>
			</Stack>
		</BasicDialog>
	);
};

export default AppVersionDialog;
